import { Component, OnChanges, OnInit, OnDestroy, SimpleChanges, inject, output, viewChild, input, computed, effect, signal } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { BehaviorSubject, Subject, Subscription, delay, from, concatMap, map, tap } from "rxjs";
import { Store, select } from "@ngrx/store";
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import {
  faImages,
  faDownload,
  faTrashAlt,
  faEye,
  faEyeSlash,
  faMagnifyingGlass,
  faTags,
  faUser
} from '@fortawesome/pro-light-svg-icons'; // 'fal' (Light version)

import { AppConfig, APP_CONFIG_TOKEN } from "libs/config/app.lib.config";
import { ConfirmDialogComponent } from "libs/ux-components/src/lib/components/confirm-dialog";

import { MediaService } from "../../services";
import { DeleteMedia, MediaModel, MediaType, UpdateMedia, MediaVisibilityScope, AnamneseUploadType, AnamneseFunctionALUploadType, DefaultAllowedExtensions } from "../../store";
import { Update } from "@ngrx/entity";
import { selectIsAdmin, selectIsSuperAdmin } from "libs/auth/src/lib/store/auth.selectors";
import { AppState } from "libs/tm-common/src/lib/store/reducers/reducers";
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslateModule } from "@ngx-translate/core";
import { MatIconModule } from '@angular/material/icon';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { CommonModule } from "@angular/common";
import { MatButtonModule } from "@angular/material/button";
import { MatTableDataSource, MatTableModule } from "@angular/material/table";
import { MatSort, MatSortModule } from "@angular/material/sort";
import { ActivatedRoute } from "@angular/router";
import { FilePreviewService } from "../../services/file-preview.lib.service";
import { ClientName } from "libs/config/client.names";
import { faDiagramProject } from "@fortawesome/free-solid-svg-icons";
import { toSignal } from "@angular/core/rxjs-interop";
import { FormsModule } from "@angular/forms";
import { MatInputModule } from "@angular/material/input";

const UploadTypeLabelKeys: Record<AnamneseUploadType | AnamneseFunctionALUploadType, string> = {
	[AnamneseUploadType.UJ]: "UplType.uj",
	[AnamneseUploadType.LJ]: "UplType.lj",
	[AnamneseUploadType.MISC_DVT]: "UplType.dvt",
	[AnamneseFunctionALUploadType.UJ_BITE]: "UplType.uj",
	[AnamneseFunctionALUploadType.LJ_BITE]: "UplType.lj",
	[AnamneseFunctionALUploadType.UJ_REGISTRATION]: "UplType.uj_registration",
	[AnamneseFunctionALUploadType.LJ_REGISTRATION]: "UplType.lj_registration",
	[AnamneseFunctionALUploadType.EXTRAORAL_FRONT_MOUTH_CLOSED]: "UplType.extra",
	[AnamneseFunctionALUploadType.EXTRAORAL_FRONT_MOUTH_OPEN]: "UplType.extra",
	[AnamneseFunctionALUploadType.EXTRAORAL_FRONT_SMILE]: "UplType.extra",
	[AnamneseFunctionALUploadType.EXTRAORAL_RIGHT]: "UplType.extra",
	[AnamneseFunctionALUploadType.INTRAORAL_FRONT]: "UplType.intra",
	[AnamneseFunctionALUploadType.INTRAORAL_UJ]: "UplType.intra",
	[AnamneseFunctionALUploadType.INTRAORAL_LJ]: "UplType.intra",
	[AnamneseFunctionALUploadType.INTRAORAL_RIGHT]: "UplType.intra",
	[AnamneseFunctionALUploadType.INTRAORAL_LEFT]: "UplType.intra",
	[AnamneseFunctionALUploadType.MISC_OPG]: "UplType.opg",
	[AnamneseFunctionALUploadType.MISC_FRS]: "UplType.frs"
};

const BASE_COLUMNS = ['checkbox', 'filename', 'uploadType', 'fileType', 'mediaType', 'filesize', 'created', 'preview', 'download'] as const;

@Component({
    selector: "lib-file-list",
    templateUrl: "./file-list.component.html",
    styleUrls: ["./_file-list.component.scss"],
    imports: [
		FontAwesomeModule,
		MatTooltipModule,
		TranslateModule,
		MatIconModule,
		MatCheckboxModule,
		MatButtonModule,
		MatTableModule,
		MatSortModule,
		CommonModule,
		FormsModule,
		MatInputModule
	]
})
export class FileListComponent implements OnInit, OnChanges, OnDestroy {
	private store = inject<Store<AppState>>(Store);
	private dialog = inject(MatDialog);
	private mediaService = inject(MediaService);
	private route = inject(ActivatedRoute);
	private filePreviewService = inject(FilePreviewService);
	protected config = inject<AppConfig>(APP_CONFIG_TOKEN);

	faMagnifyingGlass = faMagnifyingGlass;
	faDownload = faDownload;
	faEye = faEye;
	faEyeSlash = faEyeSlash;
	faImages = faImages;
	faTrashAlt = faTrashAlt;
	faDiagramProject = faDiagramProject;
	faTags = faTags;
	faUser = faUser;

	readonly media = input<MediaModel[]>(undefined);
	readonly treatmentId = input<string>(undefined);
	readonly ticketId = input<string>(undefined);
	readonly refreshAction = output();
	MediaTypes = MediaType;

	selectedMediaFiles: {id: string, orgFilename: string}[] = [];
	allFilesChecked = signal<boolean>(false);
	searchQuery = '';
	showAssociatedTreatmentFiles = new BehaviorSubject<boolean>(false);
	hasAssociatedTreatmentFiles = false;
	VisibilityScope = MediaVisibilityScope;

	protected isRefreshButtonDisabled = false;
	protected refreshButtonClicked = new Subject<void>();
	private refreshButtonSubscription: Subscription;

	protected readonly sort = viewChild(MatSort);
	protected dataSource = new MatTableDataSource<MediaModel>([]);

	private isAllowedToHide = toSignal(
		this.store.pipe(select(selectIsAdmin)),
		{ initialValue: false }
	);
	private isAllowedToDelete = toSignal(
		this.store.pipe(select(selectIsSuperAdmin), map(isSuperAdmin => this.checkDeletionPermission(isSuperAdmin))),
		{ initialValue: false }
	);

	protected displayedColumns = computed<string[]>(() => {
		const columns: string[] = [...BASE_COLUMNS];
		if (this.isAllowedToHide()) {
			columns.push("hide");
		}
		if (this.isAllowedToDelete()){
			columns.push("delete");
		}
		return columns;
	});

	readonly isTicketComponent = computed<boolean>(() => !!this.ticketId());

	constructor() {
		this.refreshButtonSubscription = this.refreshButtonClicked
			.pipe(
				tap(() => (this.isRefreshButtonDisabled = true)),
				delay(500),
				tap(() => (this.isRefreshButtonDisabled = false))
			)
			.subscribe();

		this.dataSource.sortingDataAccessor = (file: MediaModel, column: string) => {
			switch (column) {
				case "filename":
					return file.orgFilename?.toLowerCase() ?? "";
				case "uploadType":
					return this.getUploadTypeLabel(file.uploadType)?.toLowerCase() ?? "";
				case "fileType":
					return file.filetype?.toLowerCase() ?? "";
				case "mediaType":
					return file.type;
				case "filesize":
					return file.filesize ?? 0;
				case "created":
					return file.created ? new Date(file.created).getTime() : 0;
				default:
					return "";
			}
		};

		effect(() => {
			const sort = this.sort();
			if (sort){
				this.dataSource.sort = sort;
			}
		});
	}

	ngOnDestroy(): void {
		this.refreshButtonSubscription.unsubscribe();
	}

	ngOnInit(): void {
		this.showAssociatedTreatmentFiles.asObservable().subscribe(() => {
			this.resetSelectedFiles();
			this.dataSource.data = this.getDisplayedMedia();
		});
	}

	ngOnChanges(changes: SimpleChanges) {
		this.resetSelectedFiles();
		if (changes.media) {
			this.dataSource.data = this.getDisplayedMedia();
			if (this.isTicketComponent()) {
				this.hasAssociatedTreatmentFiles = false;
			} else {
				this.hasAssociatedTreatmentFiles = this.media().some((file) => file.treatment !== this.treatmentId());
			}
		}
	}

	openDeleteConfirmationDialog(mediaId: string) {
		const dialogData = {
			title: "Confirm deletion",
			msg: "Are you sure you want to delete the selected file?",
			no: "No",
			yes: "Yes"
		};
		const dialogRef = this.dialog.open(ConfirmDialogComponent, {
			data: dialogData
		});

		dialogRef.afterClosed().subscribe((result) => {
			if (result == "yes") {
				this.deleteMediaFile(mediaId);
			}
		});
	}

	downloadMediaFile(id: string, orgFilename: string) {
		this.mediaService.getFile(id, orgFilename).subscribe();
	}

	onDownloadSelectedFilesClicked() {
		from(this.selectedMediaFiles).pipe(
			concatMap(({id, orgFilename}) => this.mediaService.getFile(id, orgFilename).pipe(delay(500)))
		).subscribe();
	}

	openFilePreview(file: MediaModel) {
		this.filePreviewService.openFilePreview(file);
	}

	deleteMediaFile(id: string) {
		//only update treatment if the component is associated with a treatment
		const treatmentId = this.isTicketComponent() ? undefined : this.route.snapshot.params.id;
		this.store.dispatch(new DeleteMedia({ id: id, treatmentId: treatmentId}));
	}

	checkDeletionPermission(isSuperAdmin: boolean): boolean {
		if (this.config.app === ClientName.TM) {
			return false;
		}
		return isSuperAdmin;
	}

	hideFile(file: MediaModel) {
		const newVisiblity: MediaVisibilityScope =
			file.visibilityScope === MediaVisibilityScope.PUBLIC
				? MediaVisibilityScope.INTERN
				: MediaVisibilityScope.PUBLIC;
		const updatedModel: Update<MediaModel> = {
			id: file._id,
			changes: { visibilityScope: newVisiblity }
		};
		this.store.dispatch(new UpdateMedia({ media: updatedModel }));
	}

	getLocale() {
		return this.config.locale;
	}

	getUploadTypeLabel(uploadType?: string): string {
		return UploadTypeLabelKeys[uploadType] ?? uploadType;
	}

	getFileSizeAsString(bytes: number) {
		return this.mediaService.getFileSizeAsString(bytes);
	}

	onFileSelectionChange(isChecked: boolean, file: MediaModel) {
		if (isChecked) {
			this.selectedMediaFiles.push({id: file._id, orgFilename: file.orgFilename});
		} else {
			this.selectedMediaFiles = this.selectedMediaFiles.filter(({id}) => id !== file._id);
		}
		this.allFilesChecked.set(this.selectedMediaFiles.length === this.media().length);
	}

	onAllFileSelectionChange(isChecked: boolean) {
		this.selectedMediaFiles = [];
		if (isChecked) {
			this.dataSource.filteredData.forEach(element => {
				this.selectedMediaFiles.push({id: element._id, orgFilename: element.orgFilename});
			});
		}
		this.allFilesChecked.set(isChecked);
	}

	isFileSelected(file: MediaModel) {
		return this.selectedMediaFiles.some(({id}) => id === file._id);
	}

	areSomeFilesSelected() {
		return this.selectedMediaFiles.length > 0 && !this.allFilesChecked();
	}

	onRefreshClicked() {
		this.refreshAction.emit();
		this.refreshButtonClicked.next();
	}

	protected isFilePreviewAvailable(filetype: string): boolean {
		return this.filePreviewService.isFilePreviewAvailable(filetype);
	}

	protected isMultipleFilesPreviewButtonDisabled(): boolean {
		return (
			this.selectedMediaFiles.length < 2 ||
			this.selectedMediaFiles.length > 4 ||
			this.selectedMediaFiles.some(({id}) => {
				const filetype = this.dataSource.filteredData.find((file) => file._id === id)?.filetype;
				return ![DefaultAllowedExtensions.STL, DefaultAllowedExtensions.PLY].includes(
					filetype?.toLowerCase() as DefaultAllowedExtensions
				);
			})
		);
	}

	protected onMultipleFilesPreviewButtonClicked() {
		const files: MediaModel[] = this.selectedMediaFiles.map(({id}) =>
			this.dataSource.filteredData.find((file) => file._id === id)
		);
		this.filePreviewService.openFilePreviewMultiple3DModels(files);
	}

	protected isFileFromAssociatedTreatment(file: MediaModel): boolean {
		if(this.isTicketComponent()){
			return false;
		}
		return file.treatment !== this.treatmentId();
	}

	protected isFileFromAssociatedTicket(file: MediaModel): boolean {
		if(this.isTicketComponent()){
			return false;
		}
		return !!file.ticket;
	}

	protected onShowAssociatedTreatmentFilesClicked(isChecked: boolean) {
		this.showAssociatedTreatmentFiles.next(isChecked);
	}

	protected getDisplayedMedia(): MediaModel[] {
		if(this.isTicketComponent()){
			return this.media();
		}

		return this.showAssociatedTreatmentFiles.value ?
			this.media() :
			this.media().filter((file) => file.treatment === this.treatmentId());
	}

	private resetSelectedFiles(): void {
		this.selectedMediaFiles = [];
		this.allFilesChecked.set(false);
	}

	protected isFileGreyedOut(file: MediaModel): boolean {
		if ( this.isTicketComponent() ) {
			return false;
		}
		return file.visibilityScope !== MediaVisibilityScope.PUBLIC;
	}

	protected applyFilter(value: string) {
		this.dataSource.filter = value.trim().toLowerCase();
		this.resetSelectedFiles();
	}

	protected clearFilter() {
		this.searchQuery = '';
		this.dataSource.filter = '';
		this.resetSelectedFiles();
	}
}
